/**
 * Reading timer — read the same easy text again and beat your own time.
 *
 * The comparison is always to her own previous runs, never to anyone else, and
 * `celebrateImprovementOnly` keeps a slower run silent rather than marked. The
 * target must be reachable: with history on file it may not be slower than her
 * best, or "beat your time" is not a real target.
 */

import { z } from 'zod'
import { LabelSchema, NonEmptyString, SentenceSchema, specFor } from '../common'

export const ReadingContentSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('word_list'),
    words: z.array(NonEmptyString.max(24)).min(3).max(40),
  }),
  z.object({
    mode: z.literal('passage'),
    text: NonEmptyString.max(1200),
    wordCount: z.number().int().min(3).max(300),
  }),
])

export const ReadingTimerConfigSchema = z
  .object({
    title: LabelSchema,
    content: ReadingContentSchema,
    /** Seconds. Beating this is the win. */
    targetSeconds: z.number().min(5).max(600),
    /** Her own earlier runs, oldest first. */
    previousTimesSeconds: z.array(z.number().min(1).max(1200)).max(20).default([]),
    attemptsThisSession: z.number().int().min(1).max(5).default(3),
    /** Reading to someone rides her caregiving instinct — it doubles engagement. */
    readToAudience: z.object({
      enabled: z.boolean(),
      audience: LabelSchema.optional(),
    }),
    /** A slower run passes without comment. */
    celebrateImprovementOnly: z.boolean().default(true),
    /** Show the running clock, or keep it hidden until she finishes. */
    showLiveTimer: z.boolean().default(false),
    coachNote: SentenceSchema.optional(),
  })
  .superRefine((config, ctx) => {
    if (config.readToAudience.enabled && !config.readToAudience.audience) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['readToAudience', 'audience'],
        message: 'say who she is reading to',
      })
    }
    if (config.previousTimesSeconds.length > 0) {
      const best = Math.min(...config.previousTimesSeconds)
      if (config.targetSeconds > best) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['targetSeconds'],
          message: `her best is already ${best}s, so ${config.targetSeconds}s is not a target to beat`,
        })
      }
    }
    if (config.content.mode === 'passage') {
      const actual = config.content.text.split(/\s+/).filter((word) => word.length > 0).length
      if (actual !== config.content.wordCount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['content', 'wordCount'],
          message: `the passage has ${actual} words, not ${config.content.wordCount}`,
        })
      }
    }
  })

export type ReadingTimerConfig = z.infer<typeof ReadingTimerConfigSchema>

export const ReadingTimerSpecSchema = specFor('reading_timer', ReadingTimerConfigSchema)
